import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { duration } from './primitives/tokens';
import { Portal } from './primitives/Portal';
import { Icon } from './Icon';
import Input from './Input';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import styles from './ToolPanel.module.css';

export interface ToolPanelSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

export interface ToolPanelProps {
  /** Controls visibility */
  open: boolean;
  /** Called when the panel requests close (outside click, Escape) */
  onClose: () => void;
  /** Panel title shown in the header — also used as aria-label when hideHeader is true */
  title: string;
  /** When true, the title header bar is hidden (useful when children supply their own header/tabs) */
  hideHeader?: boolean;
  /** Scrollable body content */
  children: React.ReactNode;
  /** When provided, renders a search input below the header */
  search?: ToolPanelSearchProps;
  /** Ref of the trigger element — determines horizontal position (right edge used for left offset) */
  triggerRef?: React.RefObject<HTMLElement | null>;
  /** When provided, panel top aligns to this element's top instead of centering on triggerRef.
   *  Use this to anchor to the toolbar container rather than the individual button. */
  verticalAnchorRef?: React.RefObject<HTMLElement | null>;
  /** Additional className applied to the root panel element */
  className?: string;
}

const PANEL_WIDTH = 360;
const PANEL_OFFSET = 12; // gap between toolbar edge and panel (px)

/**
 * ToolPanel — a floating side panel anchored to a toolbar button.
 *
 * Renders to the right of its triggerRef (or top-left if no ref is provided).
 * Vertically centres on the trigger element, clamped to the viewport.
 * Closes on outside click or Escape.
 *
 * Used by CanvasEditor to back toolbar tools that need a richer picker
 * than a small popover can offer. Today no toolbar button calls it —
 * the component and wiring exist for future tool migrations.
 */
export function ToolPanel({
  open,
  onClose,
  title,
  hideHeader = false,
  children,
  search,
  triggerRef,
  verticalAnchorRef,
  className,
}: ToolPanelProps) {
  const titleId = useId();
  const shouldReduce = useReducedMotion();
  const panelRef = useRef<HTMLDivElement>(null);

  // Computed position — recalculated whenever `open` flips true.
  // useLayoutEffect so the panel is positioned before the browser paints,
  // avoiding a flash at (0,0).
  const [pos, setPos] = useState<{ top: number; left: number; height: number }>({ top: 0, left: 0, height: 0 });

  useLayoutEffect(() => {
    if (!open) return;

    const compute = () => {
      const trigger = triggerRef?.current;
      // Panel occupies middle 60%: 20% from top, 20% from bottom
      const top = Math.round(window.innerHeight * 0.2);
      const height = Math.round(window.innerHeight * 0.6);

      if (!trigger) {
        setPos({ top, left: 8, height });
        return;
      }
      // Use toolbar container right edge when available (wider than individual button)
      const anchor = verticalAnchorRef?.current ?? trigger;
      const anchorRect = anchor.getBoundingClientRect();
      const left = anchorRect.right + PANEL_OFFSET;

      setPos({ top, left, height });
    };

    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, [open, triggerRef, verticalAnchorRef]);

  // Escape key
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Outside click — close when clicking outside both the panel and the trigger
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (panelRef.current?.contains(target)) return;
      if (triggerRef?.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener('mousedown', handler, true);
    return () => document.removeEventListener('mousedown', handler, true);
  }, [open, onClose, triggerRef]);

  const initial = shouldReduce ? { opacity: 0 } : { opacity: 0, x: -8 };
  const animate = shouldReduce ? { opacity: 1 } : { opacity: 1, x: 0 };
  const exit = shouldReduce ? { opacity: 0 } : { opacity: 0, x: -8 };

  return (
    <AnimatePresence>
      {open && (
        <Portal>
          <motion.div
            ref={panelRef}
            role="dialog"
            aria-modal="false"
            aria-labelledby={hideHeader ? undefined : titleId}
            aria-label={hideHeader ? title : undefined}
            initial={initial}
            animate={animate}
            exit={exit}
            transition={{ duration: shouldReduce ? 0 : duration.normal / 1000, ease: 'easeOut' }}
            className={[styles.panel, className].filter(Boolean).join(' ')}
            style={{
              '--panel-top': `${pos.top}px`,
              '--panel-left': `${pos.left}px`,
              '--panel-w': `${PANEL_WIDTH}px`,
              '--panel-h': pos.height ? `${pos.height}px` : undefined,
            } as React.CSSProperties}
          >
            {/* Header — hidden when children supply their own (e.g. tabs) */}
            {!hideHeader && (
              <div className={styles.header}>
                <span id={titleId} className={styles.title}>{title}</span>
              </div>
            )}

            {/* Optional search */}
            {search && (
              <div className={styles.search}>
                <Input
                  size="sm"
                  placeholder={search.placeholder ?? 'Search…'}
                  value={search.value}
                  prefix={<Icon name="search" size={14} />}
                  allowClear
                  onChange={(e) => search.onChange(e.target.value)}
                />
              </div>
            )}

            {/* Body */}
            <div className={styles.body}>
              {children}
            </div>
          </motion.div>
        </Portal>
      )}
    </AnimatePresence>
  );
}
